import type { GuestProjectDto } from '@/core/api/models/guestProjects';
import {
  guestProjectsService,
  GUEST_PROJECTS_PAGE_SIZE,
  GUEST_PROJECTS_PAGE_SIZE_MOBILE,
} from '@/core/api/services/guestProjectsService';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform } from 'react-native';

export const GUEST_PROJECTS_QUERY_KEY = ['guest', 'projects'] as const;

const PAGE_SIZE = Platform.OS === 'web' ? GUEST_PROJECTS_PAGE_SIZE : GUEST_PROJECTS_PAGE_SIZE_MOBILE;

const EMPTY_PROJECTS: GuestProjectDto[] = [];

function flattenProjectPages(
  pages:
    | ReadonlyArray<{ details: ReadonlyArray<GuestProjectDto> }>
    | undefined,
): GuestProjectDto[] {
  if (!pages || pages.length === 0) {
    return EMPTY_PROJECTS;
  }
  const seen = new Set<string>();
  const flat: GuestProjectDto[] = [];
  for (const page of pages) {
    for (const project of page.details) {
      if (project?.project_id && !seen.has(project.project_id)) {
        seen.add(project.project_id);
        flat.push(project);
      }
    }
  }
  return flat.length > 0 ? flat : EMPTY_PROJECTS;
}

export interface UseGuestProjectsQueryResult {
  readonly projects: ReadonlyArray<GuestProjectDto>;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isSuccess: boolean;
  /** True while a next page request is in flight (background prefetch). */
  readonly isFetchingNextPage: boolean;
  /** True until every page has been fetched in the background. */
  readonly isPrefetchingCatalogue: boolean;
  /**
   * Scroll "load more" is disabled — pages are pulled in the background.
   * Always false for guest list / explore UI.
   */
  readonly canLoadMore: boolean;
  readonly loadMore: () => void;
  readonly refetch: () => void;
}

/**
 * Shared GET /v1/projects (Basic Auth) for guest list + details/explore.
 * Loads the first page (`GUEST_PROJECTS_PAGE_SIZE` on web,
 * `GUEST_PROJECTS_PAGE_SIZE_MOBILE` on native), then keeps fetching the
 * next pages in the background until `hasNext` is false.
 */
export function useGuestProjectsQuery(): UseGuestProjectsQueryResult {
  const query = useInfiniteQuery({
    queryKey: GUEST_PROJECTS_QUERY_KEY,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const response = await guestProjectsService.getProjects({
        page: pageParam,
        limit: PAGE_SIZE,
      });
      const details = Array.isArray(response.details) ? response.details : [];
      return {
        ...response,
        page: response.page ?? pageParam,
        details,
      };
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.hasNext === false || String(lastPage.hasNext) === 'false') {
        return undefined;
      }
      if (!lastPage.details || lastPage.details.length === 0) {
        return undefined;
      }

      const previousPages = allPages.slice(0, -1);
      if (previousPages.length > 0) {
        const seenIds = new Set(
          previousPages.flatMap((p) => p.details.map((d) => d.project_id)),
        );
        const hasNew = lastPage.details.some(
          (d) => d?.project_id && !seenIds.has(d.project_id),
        );
        if (!hasNew) {
          return undefined;
        }
      }

      return lastPageParam + 1;
    },
    staleTime: 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const projects = useMemo(
    () => flattenProjectPages(query.data?.pages),
    [query.data?.pages],
  );

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isError, isSuccess } =
    query;
  const isLoadingPageRef = useRef(false);

  const loadMore = useCallback(() => {
    if (isLoadingPageRef.current || !hasNextPage || isFetchingNextPage) {
      return;
    }
    isLoadingPageRef.current = true;
    void fetchNextPage({ cancelRefetch: false }).finally(() => {
      isLoadingPageRef.current = false;
    });
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // After the first page is ready, keep pulling remaining pages until exhausted.
  useEffect(() => {
    if (!isSuccess || isError) {
      return;
    }
    if (!hasNextPage || isFetchingNextPage || isLoadingPageRef.current) {
      return;
    }
    loadMore();
  }, [
    isSuccess,
    isError,
    hasNextPage,
    isFetchingNextPage,
    loadMore,
    query.data?.pages.length,
  ]);

  const isPrefetchingCatalogue = Boolean(hasNextPage) || isFetchingNextPage;

  return {
    projects,
    isLoading: query.isPending && projects.length === 0,
    isError: query.isError && projects.length === 0,
    isSuccess: query.isSuccess,
    isFetchingNextPage,
    isPrefetchingCatalogue,
    canLoadMore: false,
    loadMore,
    refetch: () => {
      void query.refetch();
    },
  };
}
